import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {
    AmaronEventContainerDispositionEnum,
    amaronEventContainerDispositions,
} from "./EventContainers";

export type AmaronPriorityLevel = "P0" | "P1" | "P2" | "P3";

export type AmaronPrioritySourceConfig = {
    columnIndex: number;
    exactRawPatterns?: Array<string>;
    key: string;
    matchPatterns: Array<string>;
    priority: AmaronPriorityLevel;
};

export const AMARON_PRIORITY_SCORE_RANGES: Record<AmaronPriorityLevel, {max: number; min: number}> = {
    P0: {max: 54, min: 0},
    P1: {max: 109, min: 55},
    P2: {max: 164, min: 110},
    P3: {max: Number.POSITIVE_INFINITY, min: 165},
};

export const AMARON_PRIORITY_SOURCE_CONFIGS: Array<AmaronPrioritySourceConfig> = [
    {
        columnIndex: 0,
        key: "incoming-call",
        matchPatterns: ["inbound call", "inbound calls", "incoming call", "incoming calls", "inbound missed", "inbound missed call"],
        priority: "P0",
    },
    {columnIndex: 1, key: "whatsapp-incoming", matchPatterns: ["whatsapp", "whatsapp incoming"], priority: "P0"},
    {columnIndex: 2, key: "google-ads", matchPatterns: ["google", "google ads"], priority: "P0"},
    {
        columnIndex: 3,
        key: "meta-on-form",
        matchPatterns: ["meta", "meta ads", "meta onform", "meta on form", "meta on-form"],
        priority: "P1",
    },
    {columnIndex: 4, key: "meta-website", matchPatterns: ["fb", "ig", "meta website"], priority: "P1"},
    {
        columnIndex: 5,
        exactRawPatterns: ["WhatsApp_CRM"],
        key: "wa-crm-live",
        matchPatterns: ["wa crm live", "whatsapp crm live"],
        priority: "P1",
    },
    {
        columnIndex: 6,
        exactRawPatterns: ["WhatsApp CRM"],
        key: "wa-crm-historical",
        matchPatterns: ["wa crm historical", "whatsapp crm historical"],
        priority: "P1",
    },
    {columnIndex: 7, key: "btl", matchPatterns: ["btl", "employee activation"], priority: "P2"},
    {columnIndex: 8, key: "pos", matchPatterns: ["pos"], priority: "P2"},
    {columnIndex: 9, key: "organic-website", matchPatterns: ["null", "unknown", "organic website"], priority: "P2"},
    {columnIndex: 10, key: "default", matchPatterns: [], priority: "P3"},
];

export const AMARON_PRIORITY_DISPOSITION_BASE_SCORE_BY_ENUM: Record<AmaronEventContainerDispositionEnum, number> = {
    [AmaronEventContainerDispositionEnum.NEW_LEAD]: 0,
    [AmaronEventContainerDispositionEnum.NEW_OPPORTUNITY]: 44,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_INTERESTED_FOLLOW_UP]: 11,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_INTERESTED_NEED_MORE_INFORMATION]: 22,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_INTERESTED_OTHER]: 33,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_INTERESTED_SLOT_NOT_AVAILABLE]: 55,
    [AmaronEventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER_CBL1]: 66,
    [AmaronEventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER_CBL2]: 77,
    [AmaronEventContainerDispositionEnum.CONNECTED_CALL_BACK_LATER_CBL3]: 88,
    [AmaronEventContainerDispositionEnum.NOT_CONNECTED_NR1]: 99,
    [AmaronEventContainerDispositionEnum.NOT_CONNECTED_NR2]: 110,
    [AmaronEventContainerDispositionEnum.NOT_CONNECTED_NR3]: 121,
    [AmaronEventContainerDispositionEnum.NOT_CONNECTED_NR4]: 132,
    [AmaronEventContainerDispositionEnum.NOT_CONNECTED_NR5]: 143,
    [AmaronEventContainerDispositionEnum.NOT_CONNECTED_NR6]: 154,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_SHORT_HANG_UP]: 154,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_TRANSFERRED_TO_VOICEMAIL]: 165,
    [AmaronEventContainerDispositionEnum.NOT_CONNECTED_CALL_HUNG_UP]: 165,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_INTERESTED_BOOKED]: 165,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_NOT_INTERESTED_GENERAL_QUERY]: 165,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_LANGUAGE_ISSUE]: 165,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_NOT_INTERESTED_NEW_CAR]: 165,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_NOT_INTERESTED_NO_REQUIREMENT_SHARED]: 176,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_NOT_INTERESTED_OEM_PREFERRED]: 176,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_NOT_INTERESTED_PRICE_ISSUE]: 176,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_NOT_INTERESTED_SERVICE_NOT_DUE]: 176,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_UNSERVICEABLE_AREA_NOT_COVERED]: 176,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_UNSERVICEABLE_CAR_NOT_SUPPORTED]: 187,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_UNSERVICEABLE_IMMEDIATE_SERVICE]: 198,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_UNSERVICEABLE_RSA_REQUEST]: 209,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_UNSERVICEABLE_NO_CAR_DETAILS]: 198,
    [AmaronEventContainerDispositionEnum.NOT_CONNECTED_INVALID]: 220,
    [AmaronEventContainerDispositionEnum.CONNECTED_INVALID_DID_NOT_FILL]: 231,
    [AmaronEventContainerDispositionEnum.CONNECTED_INVALID_FILLED_BY_MISTAKE]: 242,
    [AmaronEventContainerDispositionEnum.WRONG_NUMBER]: 253,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_CUSTOMER_ESCALATION_MARSHAL_NOT_REACHING_ON_TIME]: 264,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_CUSTOMER_ESCALATION_MARSHAL_NUMBER_NOT_SHARED]: 275,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_CUSTOMER_ESCALATION_MARSHAL_DENIED_FOR_SERVICE]: 286,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_CUSTOMER_ESCALATION_MARSHAL_GAVE_ALTERNATE_NUMBER]: 297,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_CUSTOMER_ESCALATION_REFUND_NOT_RECEIVED]: 308,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_CUSTOMER_ESCALATION_CUSTOMER_WANTS_REFUND]: 319,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_CUSTOMER_ESCALATION_NOT_SATISFIED_WITH_SERVICES]: 330,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_CUSTOMER_ESCALATION_NOT_USING_APPROPRIATE_PRODUCT]: 341,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_CUSTOMER_ESCALATION_USING_DIFFERENT_PRODUCT_THAN_RECOMMENDED]: 352,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_ALREADY_BOOKED]: 363,
    [AmaronEventContainerDispositionEnum.CONNECTED_VALID_JOB_RELATED_QUERY]: 374,
};

export const AMARON_PRIORITY_DISPOSITION_BASE_SCORE_BY_ID: Record<Uuid, number> = Object.fromEntries(
    Object.entries(AMARON_PRIORITY_DISPOSITION_BASE_SCORE_BY_ENUM).map(([rawDisposition, baseScore]) => {
        const disposition = Number(rawDisposition) as AmaronEventContainerDispositionEnum;
        return [amaronEventContainerDispositions[disposition].id, baseScore];
    }),
) as Record<Uuid, number>;

const DEFAULT_SOURCE_CONFIG = AMARON_PRIORITY_SOURCE_CONFIGS[AMARON_PRIORITY_SOURCE_CONFIGS.length - 1]!;

const normalizeUtmSourceWord = (word: string): string => {
    if (word.length <= 3) {
        return word;
    }
    if (word.endsWith("ies") && word.length > 4) {
        return `${word.slice(0, -3)}y`;
    }
    if (word.endsWith("es") && word.length > 4) {
        const stem = word.slice(0, -2);
        if (stem.endsWith("s") || stem.endsWith("x") || stem.endsWith("z") || stem.endsWith("ch") || stem.endsWith("sh")) {
            return stem;
        }
    }
    if (word.endsWith("s") && word.endsWith("ss") !== true) {
        return word.slice(0, -1);
    }
    return word;
};

export const normalizeAmaronUtmSource = (raw: string): string =>
    raw
        .trim()
        .toLowerCase()
        .replaceAll(/[_-]+/g, " ")
        .replaceAll(/\s+/g, " ")
        .split(" ")
        .map(normalizeUtmSourceWord)
        .join(" ");

export const resolveAmaronPrioritySourceConfig = (
    utmSource: string | null | undefined,
): AmaronPrioritySourceConfig => {
    const raw = utmSource === null ? "null" : utmSource ?? "";
    const rawKey = raw.trim().toLowerCase();
    const normalized = normalizeAmaronUtmSource(raw);
    if (normalized === "") {
        return DEFAULT_SOURCE_CONFIG;
    }
    for (const config of AMARON_PRIORITY_SOURCE_CONFIGS) {
        if (config.exactRawPatterns?.some((pattern) => rawKey === pattern.trim().toLowerCase()) === true) {
            return config;
        }
    }
    for (const config of AMARON_PRIORITY_SOURCE_CONFIGS) {
        if (config.matchPatterns.some((pattern) => normalized === normalizeAmaronUtmSource(pattern))) {
            return config;
        }
    }
    return DEFAULT_SOURCE_CONFIG;
};

export const resolveAmaronPriorityDispositionBaseScore = (dispositionId: string): number | null =>
    AMARON_PRIORITY_DISPOSITION_BASE_SCORE_BY_ID[dispositionId as Uuid] ?? null;

export const resolveAmaronPriorityScore = (
    utmSource: string | null | undefined,
    dispositionId: string,
): number | null => {
    const baseScore = resolveAmaronPriorityDispositionBaseScore(dispositionId);
    return baseScore === null ? null : baseScore + resolveAmaronPrioritySourceConfig(utmSource).columnIndex;
};
